import i18n from '../i18n'
import { crmFetch, dataOf } from '../chat/api'
import type {
  AdoptableList,
  AllGroup,
  GroupResult,
  AdoptPreview,
  Catalog,
  CopyResult,
  CrmAccount,
  DynamicList,
  Facts,
  ListDraft,
  Preview,
  Rule,
} from './types'

/** What the Dynamic lists page calls (CRM routes, super admin only); tests pass fakes. */
export interface DynamicListsApi {
  accounts: () => Promise<CrmAccount[]>
  catalog: () => Promise<Catalog>
  list: (accountId: string) => Promise<{ lists: DynamicList[]; facts: Facts; list_names?: Record<string, string>; link_names?: Record<string, string> }>
  /** Every Account's lists, grouped by rule (database only). */
  all: () => Promise<{ groups: AllGroup[] }>
  preview: (accountId: string, rule: Rule, ofListId?: string | null) => Promise<Preview>
  create: (accountId: string, draft: ListDraft & { enabled?: boolean }) => Promise<DynamicList>
  update: (id: string, patch: Partial<ListDraft> & { enabled?: boolean }) => Promise<DynamicList>
  remove: (id: string) => Promise<{ deleted: string; kept_of_list_id: string | null }>
  rebuild: (id: string, confirmRemovals?: boolean) => Promise<DynamicList>
  adoptable: (accountId: string) => Promise<{ lists: AdoptableList[]; facts: Facts }>
  adoptPreview: (accountId: string, ofListId: string, rule: Rule) => Promise<AdoptPreview>
  /** What turning on an adopted (not yet updated) Dynamic list would do, with its own rule. */
  adoptedPreview: (id: string) => Promise<AdoptPreview>
  adopt: (accountId: string, ofListId: string, draft: Omit<ListDraft, 'name'>) => Promise<DynamicList>
  undoAdoption: (id: string) => Promise<{ released?: string; undo?: DynamicList }>
  copy: (id: string, accountIds: string[], enable: boolean) => Promise<{ results: CopyResult[] }>
  /** The Account's tracking links (ADR 0032), deleted ones included, for "Tracking link", and when they were last read from OnlyFans. */
  trackingLinks: (accountId: string) => Promise<{ links: Array<{ id: string; name: string }>; syncedAt: string | null }>
  /** Ask the worker to re-read the Account's tracking links from OnlyFans. */
  refreshTrackingLinks: (accountId: string) => Promise<unknown>
  /** One edit on several Accounts' copies of a rule; In list ids written against sourceId's Account. */
  updateGroup: (sourceId: string, ids: string[], patch: Partial<ListDraft> & { enabled?: boolean }) => Promise<{ results: GroupResult[] }>
}

async function call<T>(path: string, body: Record<string, unknown> | null, accountId = ''): Promise<T> {
  const res = await crmFetch(path, body ? { accountId, local: true, body } : { accountId, local: true, method: 'GET' })
  return dataOf<T>(res)
}

/** The api with its Account picker narrowed to `ids` (the Tool's Accounts, GET /auth/me/tools); null = every Account. */
export function withToolAccounts<A extends { accounts: () => Promise<CrmAccount[]> }>(api: A, ids: string[] | null | undefined): A {
  if (ids == null) return api
  const allowed = new Set(ids.map(String))
  return { ...api, accounts: async () => (await api.accounts()).filter((a) => allowed.has(String(a.account_id))) }
}

export const dynamicListsApi: DynamicListsApi = {
  accounts: async () => (await call<{ list: CrmAccount[] }>('/v1/accounts', null)).list,
  catalog: () => call('/v1/dynamic-lists/catalog', null),
  list: (accountId) => call('/v1/dynamic-lists/list', { account_id: accountId }, accountId),
  all: () => call('/v1/dynamic-lists/all', {}),
  preview: (accountId, rule, ofListId) =>
    call('/v1/dynamic-lists/preview', { account_id: accountId, rule, of_list_id: ofListId ?? null }, accountId),
  create: (accountId, draft) => call('/v1/dynamic-lists/create', { account_id: accountId, ...draft }, accountId),
  update: (id, patch) => call('/v1/dynamic-lists/update', { id, ...patch }),
  remove: (id) => call('/v1/dynamic-lists/delete', { id }),
  rebuild: (id, confirmRemovals = false) => call('/v1/dynamic-lists/rebuild', { id, confirm_removals: confirmRemovals }),
  adoptable: (accountId) => call('/v1/dynamic-lists/adoptable', { account_id: accountId }, accountId),
  adoptPreview: (accountId, ofListId, rule) =>
    call('/v1/dynamic-lists/adopt-preview', { account_id: accountId, of_list_id: ofListId, rule }, accountId),
  adoptedPreview: (id) => call('/v1/dynamic-lists/adopt-preview', { id }),
  adopt: (accountId, ofListId, draft) =>
    call('/v1/dynamic-lists/adopt', { account_id: accountId, of_list_id: ofListId, ...draft }, accountId),
  undoAdoption: (id) => call('/v1/dynamic-lists/undo-adoption', { id }),
  copy: (id, accountIds, enable) => call('/v1/dynamic-lists/copy', { id, account_ids: accountIds, enable }),
  updateGroup: (sourceId, ids, patch) => call('/v1/dynamic-lists/update-group', { source_id: sourceId, ids, ...patch }),
  // Both kinds (ADR 0039): a trial link's id is "trial:<id>", its name marked as a trial.
  trackingLinks: async (accountId) => {
    type Listed = {
      links: Array<{ link_id: string; name: string; is_deleted: boolean }>
      sync?: Array<{ account_id: string; last_synced_at: string | null }>
    }
    const read = (kind: 'tracking' | 'trial') =>
      call<Listed>('/v1/tracking-links/list', { account_id: accountId, kind, include_deleted: true, archived: 'any', limit: 500 }, accountId)
    const [tracking, trial] = await Promise.all([read('tracking'), read('trial')])
    const named = (l: Listed['links'][number]) => (l.is_deleted ? `${l.name} ×` : l.name)
    return {
      links: [
        ...tracking.links.map((l) => ({ id: String(l.link_id), name: named(l) })),
        ...trial.links.map((l) => ({ id: `trial:${l.link_id}`, name: i18n.t('dynamicLists:param.trialLinkName', { name: named(l) }) })),
      ],
      syncedAt: tracking.sync?.find((s) => s.account_id === accountId)?.last_synced_at ?? null,
    }
  },
  refreshTrackingLinks: (accountId) => call('/v1/tracking-links/refresh', { account_id: accountId }, accountId),
}
